import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ArrowUpRight, Quote } from "lucide-react";
import { Reveal } from "./Reveal";
import { GOOGLE_REVIEW_URL } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed 5-star row (visual). We render an accurate star display per review
// below using the numeric rating value returned by Google.
function StarRow({ value = 5, size = 16 }) {
  const full = Math.round(Number(value) || 0);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${full} out of 5`}>
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={size}
          className={i < full ? "text-hotpink fill-hotpink" : "text-white/15"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const initials = (review.author || "G")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <article
      data-testid="google-review-card"
      className="relative snap-start shrink-0 w-[85%] sm:w-[420px] lg:w-[440px] rounded-2xl border border-white/10 bg-[#121212] p-7 md:p-8 flex flex-col"
    >
      <Quote
        size={32}
        className="absolute right-6 top-6 text-hotpink/25"
        strokeWidth={1.5}
      />

      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-full grid place-items-center bg-black border border-white/10 font-display uppercase text-sm text-white">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="font-body text-sm text-white truncate">
            {review.author || "Google user"}
          </p>
          <p className="font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
            {review.relativeTime || "Google Review"}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <StarRow value={review.rating || 5} />
      </div>

      <p className="mt-5 font-body text-[15px] md:text-base text-neutral-300 leading-relaxed line-clamp-6">
        {review.text}
      </p>

      <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between gap-2">
        <span className="font-body text-[10px] uppercase tracking-[0.28em] text-neutral-500">
          via Google
        </span>
        {review.googleMapsUri ? (
          <a
            href={review.googleMapsUri}
            target="_blank"
            rel="noreferrer"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-teal hover:text-white transition-colors inline-flex items-center gap-1"
          >
            View on Google <ArrowUpRight size={12} />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function Fallback() {
  return (
    <Reveal delay={0.2}>
      <div
        data-testid="reviews-fallback"
        className="rounded-2xl border border-white/10 bg-[#121212] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
      >
        <div className="max-w-xl">
          <StarRow value={5} size={18} />
          <p className="mt-4 font-body text-base md:text-lg text-neutral-300 leading-relaxed">
            See what our community is saying about War Room Performance on
            Google.
          </p>
        </div>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="reviews-fallback-cta"
          className="inline-flex items-center gap-2 font-display uppercase tracking-wide text-base px-8 py-4 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm whitespace-nowrap"
        >
          Read our Google Reviews <ArrowUpRight size={18} />
        </a>
      </div>
    </Reveal>
  );
}

export default function Reviews() {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`, { timeout: 9000 });
        if (cancelled) return;
        if (data && data.available && Array.isArray(data.reviews)) {
          setState({ status: "ok", data });
        } else {
          setState({ status: "fallback", data: null });
        }
      } catch (_e) {
        if (!cancelled) setState({ status: "fallback", data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const live = state.status === "ok" && state.data;
  const rating = live ? Number(state.data.rating || 0).toFixed(1) : null;
  const count = live ? state.data.userRatingCount || 0 : null;
  const reviews = live ? state.data.reviews || [] : [];
  const readAllUri =
    (live && state.data.googleMapsUri) || GOOGLE_REVIEW_URL;

  return (
    <section
      id="reviews"
      className="relative bg-ink py-24 md:py-36 px-6 lg:px-10 overflow-hidden"
      data-testid="reviews-section"
    >
      <div className="pointer-events-none absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-hotpink/10 blur-[120px]" />

      <div className="relative mx-auto max-w-[1400px]">
        <Reveal>
          <p className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-teal mb-6">
            / Google Reviews
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-700 uppercase leading-[0.9] tracking-tighter text-white text-5xl md:text-6xl lg:text-7xl mb-10">
            What people are saying
          </h2>
        </Reveal>

        {/* Loading skeleton */}
        {state.status === "loading" && (
          <div
            data-testid="reviews-loading"
            className="rounded-2xl border border-white/10 bg-[#121212] p-8 md:p-12 animate-pulse"
          >
            <div className="h-4 w-40 bg-white/10 rounded mb-6" />
            <div className="h-6 w-64 bg-white/10 rounded" />
          </div>
        )}

        {/* Live rating summary + reviews */}
        {live && (
          <>
            <Reveal delay={0.15}>
              <div
                data-testid="reviews-summary"
                className="rounded-2xl border border-white/10 bg-[#121212] p-8 md:p-10 mb-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
              >
                <div className="flex items-center gap-6">
                  <div className="flex flex-col">
                    <span
                      data-testid="reviews-rating-value"
                      className="font-display text-6xl md:text-7xl leading-none text-white tracking-tight"
                    >
                      {rating}
                    </span>
                    <div className="mt-3">
                      <StarRow value={rating} size={18} />
                    </div>
                  </div>
                  <div className="h-16 w-px bg-white/10" />
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-[0.25em] text-teal">
                      Google Rating
                    </p>
                    <p
                      data-testid="reviews-count"
                      className="mt-2 font-body text-base md:text-lg text-neutral-300"
                    >
                      Based on{" "}
                      <span className="text-white font-semibold">
                        {count}
                      </span>{" "}
                      Google Reviews
                    </p>
                  </div>
                </div>

                <div className="md:ml-auto flex flex-col sm:flex-row gap-3">
                  <a
                    href={readAllUri}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reviews-read-all-btn"
                    className="inline-flex items-center justify-center gap-2 font-display uppercase tracking-wide text-sm px-6 py-3 bg-white text-ink hover:bg-hotpink hover:text-white transition-colors duration-300 rounded-sm"
                  >
                    Read all reviews on Google <ArrowUpRight size={16} />
                  </a>
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reviews-leave-btn"
                    className="inline-flex items-center justify-center gap-2 font-display uppercase tracking-wide text-sm px-6 py-3 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm"
                  >
                    Leave us a Google Review <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>
            </Reveal>

            {reviews.length > 0 ? (
              <Reveal delay={0.2}>
                <div
                  data-testid="reviews-carousel"
                  className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {reviews.map((r, i) => (
                    <ReviewCard key={i} review={r} />
                  ))}
                </div>
              </Reveal>
            ) : (
              <Fallback />
            )}

            <p className="mt-6 font-body text-[10px] uppercase tracking-[0.25em] text-neutral-600">
              Reviews sourced live from Google. War Room Performance on Google.
            </p>
          </>
        )}

        {state.status === "fallback" && <Fallback />}
      </div>
    </section>
  );
}
